import { Gauge, GripVertical, Pencil, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

interface Metric {
  id: string;
  name: string;
  slug: string;
  definition: string | null;
  category: string | null;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function MetricsPage() {
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Metric | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Metric | null>(null);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // form state
  const [formName, setFormName] = useState("");
  const [formSlug, setFormSlug] = useState("");
  const [formDefinition, setFormDefinition] = useState("");
  const [formCategory, setFormCategory] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await fetch("/api/metrics");
      if (res.ok) setMetrics(await res.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const openCreate = () => {
    setEditing(null);
    setFormName("");
    setFormSlug("");
    setFormDefinition("");
    setFormCategory("");
    setSlugTouched(false);
    setDialogOpen(true);
  };

  const openEdit = (m: Metric) => {
    setEditing(m);
    setFormName(m.name);
    setFormSlug(m.slug);
    setFormDefinition(m.definition || "");
    setFormCategory(m.category || "");
    setSlugTouched(true);
    setDialogOpen(true);
  };

  const handleNameChange = (val: string) => {
    setFormName(val);
    if (!slugTouched) setFormSlug(slugify(val));
  };

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: formName,
        slug: formSlug,
        definition: formDefinition || null,
        category: formCategory || null,
      };

      if (editing) {
        await fetch(`/api/metrics/${editing.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        await fetch("/api/metrics", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }
      setDialogOpen(false);
      fetchMetrics();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await fetch(`/api/metrics/${deleteTarget.id}`, { method: "DELETE" });
    setDeleteTarget(null);
    fetchMetrics();
  };

  // group by category
  const grouped = metrics.reduce<Record<string, Metric[]>>((acc, m) => {
    const key = m.category || "Uncategorized";
    if (!acc[key]) acc[key] = [];
    acc[key].push(m);
    return acc;
  }, {});

  const categories = Object.keys(grouped).sort((a, b) => {
    if (a === "Uncategorized") return 1;
    if (b === "Uncategorized") return -1;
    return a.localeCompare(b);
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Metric Definitions" />
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Metric Definitions"
        description="Define and document how each metric is calculated."
        actions={
          <Button onClick={openCreate} size="sm">
            <Plus className="mr-2 h-4 w-4" />
            Add Metric
          </Button>
        }
      />

      {metrics.length === 0 ? (
        <EmptyState
          icon={Gauge}
          heading="No metrics defined yet"
          description="Add your first metric definition to document how your KPIs are calculated."
          actionLabel="Add Metric"
          onAction={openCreate}
        />
      ) : (
        <div className="space-y-8">
          {categories.map((cat) => (
            <div key={cat}>
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                {cat}
              </h2>
              <div className="space-y-2">
                {(grouped[cat] ?? []).map((m) => {
                  const isExpanded = expandedId === m.id;
                  return (
                    <Card
                      key={m.id}
                      className={cn("transition-shadow", isExpanded && "ring-1 ring-brand-gold/30")}
                    >
                      <CardContent className="p-0">
                        {/* Header row */}
                        <button
                          type="button"
                          className="flex w-full items-center gap-3 px-4 py-3 text-left"
                          onClick={() => setExpandedId(isExpanded ? null : m.id)}
                        >
                          <GripVertical className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-sm">{m.name}</span>
                            <span className="ml-2 text-xs text-muted-foreground font-mono">
                              {m.slug}
                            </span>
                          </div>
                          {m.definition ? (
                            <span className="text-xs text-muted-foreground truncate max-w-[300px] hidden sm:block">
                              {m.definition.slice(0, 80)}
                              {m.definition.length > 80 ? "..." : ""}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground/50 italic hidden sm:block">
                              No definition
                            </span>
                          )}
                        </button>

                        {/* Expanded detail */}
                        {isExpanded && (
                          <>
                            <Separator />
                            <div className="px-4 py-4 space-y-3">
                              <div>
                                <Label className="text-xs text-muted-foreground">Definition</Label>
                                <p className="mt-1 text-sm whitespace-pre-wrap">
                                  {m.definition || (
                                    <span className="text-muted-foreground/50 italic">
                                      Not yet documented.
                                    </span>
                                  )}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 pt-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEdit(m);
                                  }}
                                >
                                  <Pencil className="mr-2 h-3.5 w-3.5" />
                                  Edit
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteTarget(m);
                                  }}
                                >
                                  <Trash2 className="mr-2 h-3.5 w-3.5" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Metric" : "New Metric"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="metric-name">Name</Label>
              <Input
                id="metric-name"
                value={formName}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="e.g. Gross Sales"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="metric-slug">Slug</Label>
              <Input
                id="metric-slug"
                value={formSlug}
                onChange={(e) => {
                  setFormSlug(e.target.value);
                  setSlugTouched(true);
                }}
                placeholder="e.g. gross-sales"
                className="font-mono text-sm"
                required
              />
              <p className="text-xs text-muted-foreground">
                Unique identifier used in code and APIs.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="metric-category">Category</Label>
              <Input
                id="metric-category"
                value={formCategory}
                onChange={(e) => setFormCategory(e.target.value)}
                placeholder="e.g. Sales, Performance"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="metric-definition">Definition</Label>
              <Textarea
                id="metric-definition"
                value={formDefinition}
                onChange={(e) => setFormDefinition(e.target.value)}
                placeholder="Describe what this metric measures and how it is calculated..."
                rows={5}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Saving..." : editing ? "Save Changes" : "Create Metric"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Metric</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This action
            cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
